import { Link } from "react-router-dom";
import { FingerprintIcon as Fingerprint, WarningCircleIcon as Warning } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { batchStatusClass, batchStatusLabel, fmtDateTime, isLegacy } from "@/lib/provenanceDisplay";

/**
 * Compact provenance chip for one evidence row. Shows the source command +
 * hash prefix; click → popover with the batch facts and a link to the Import
 * History view. Rows without a batch get an explicit "Parsed" / "Unknown"
 * chip — never an invented provenance.
 */
export default function EvidenceBadge({ batch, row, workspaceId }) {
  if (!batch) {
    const parsed = row?.source === "parsed";
    return (
      <span
        className="inline-flex items-center gap-1 ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-[var(--ml-border)] text-[var(--ml-text-secondary)]"
        title={parsed ? "Derived by the source parser — not an IBM i command export" : "No import batch recorded"}
        data-testid="evidence-badge-none"
      >
        <Warning size={11} /> {parsed ? "Parsed source" : "Unknown"}
      </span>
    );
  }
  const legacy = isLegacy(batch);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`inline-flex items-center gap-1 ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border hover:opacity-80 transition-opacity ${batchStatusClass(batch.status)}`}
          title={legacy ? "Legacy row — source command inferred, not proven" : `${batch.source_command} · ${batch.original_filename || ""}`}
          data-testid="evidence-badge"
        >
          <Fingerprint size={11} weight="bold" />
          {batch.source_command}
          {legacy ? " · legacy" : batch.file_sha256_short ? ` · ${batch.file_sha256_short.slice(0, 7)}` : ""}
        </button>
      </PopoverTrigger>
      <PopoverContent side="left" align="start" className="w-80 text-sm space-y-2 bg-white border border-[var(--ml-border)] shadow-lg rounded-none" data-testid="evidence-popover">
        <div className="flex items-center justify-between gap-2">
          <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Evidence</span>
          <span className={`ml-mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border ${batchStatusClass(batch.status)}`}>
            {batchStatusLabel(batch.status)}
          </span>
        </div>
        <Fact label="Command" value={batch.source_command} mono />
        <Fact label="File" value={batch.original_filename || (legacy ? "Unknown" : "—")} />
        <Fact label="Imported" value={fmtDateTime(batch.imported_at)} />
        <Fact label="Captured" value={legacy ? "Unknown" : fmtDateTime(batch.captured_at)} />
        <Fact label="By" value={batch.imported_by || (legacy ? "Unknown" : "—")} />
        <Fact label="Rows" value={`${batch.imported_row_count ?? 0} imported · ${batch.skipped_row_count ?? 0} skipped`} />
        <Fact label="SHA-256" value={batch.file_sha256_short ? `${batch.file_sha256_short}…` : "Unknown"} mono />
        {legacy && (
          <p className="text-xs text-[#6929c4] leading-snug" data-testid="evidence-legacy-note">
            Predates provenance tracking. Command inferred from the target table — not proven.
          </p>
        )}
        <Link
          to={`/workspaces/${workspaceId}/metadata/imports?batch=${batch.id}`}
          className="inline-block text-xs text-[var(--ml-primary)] hover:underline"
          data-testid="evidence-open-history"
        >
          Open in Import History →
        </Link>
      </PopoverContent>
    </Popover>
  );
}

function Fact({ label, value, mono }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-[var(--ml-text-secondary)] shrink-0">{label}</span>
      <span className={`text-xs text-right break-all ${mono ? "ml-mono" : ""}`}>{value}</span>
    </div>
  );
}
